/**
 * Environment-specific URL utilities for the Aria chat feature
 */

export const getAriaBaseUrl = (): string => {
  if (process.env.REACT_APP_ENV === 'staging') {
    return 'https://beta.ariachat.ai';
  } else if (process.env.NODE_ENV === 'production') {
    return 'https://app.ariachat.ai';
  } else {
    // Development - include /aria for localhost
    return 'http://localhost:3000/aria';
  }
};

/**
 * Get the signup/assessment base URL for the public-facing flow
 */
export const getAriaSignupBaseUrl = (): string => {
  if (process.env.REACT_APP_ENV === 'staging') {
    return 'https://signup.ariachat.ai';
  } else if (process.env.NODE_ENV === 'production') {
    return 'https://me.ariachat.ai';
  } else {
    // Development - use localhost with /aria path
    return 'http://localhost:3000/aria';
  }
};

/**
 * Get the path prefix for Aria routes
 * Returns empty string for staging/production, '/aria' for localhost
 */
export const getAriaPathPrefix = (): string => {
  if (process.env.REACT_APP_ENV === 'staging' || process.env.NODE_ENV === 'production') {
    return '';
  } else {
    // Development - use /aria prefix
    return '/aria';
  }
};

/**
 * Get the main app URL for authenticated users
 */
export const getAriaAppBaseUrl = (): string => {
  if (process.env.REACT_APP_ENV === 'staging') {
    return 'https://beta.ariachat.ai';
  } else if (process.env.NODE_ENV === 'production') {
    return 'https://app.ariachat.ai';
  } else {
    // Development - use localhost with /aria path
    return 'http://localhost:3000/aria';
  }
};

/** Portal path for full-screen Ask Aria (same host as Aptive Index, no sidebar). */
export const PORTAL_FULL_SCREEN_CHAT_PATH = '/chat';

/**
 * True when the path is the portal full-screen chat entry (/chat or subpaths).
 */
export const isPortalFullScreenChatPath = (pathname?: string): boolean => {
  const path = pathname ?? (typeof window !== 'undefined' ? window.location.pathname : '');
  return path === PORTAL_FULL_SCREEN_CHAT_PATH || path.startsWith(`${PORTAL_FULL_SCREEN_CHAT_PATH}/`);
};

/**
 * Normalize a browser path to the inner route used by AriaChatStandalone.
 * Strips /aria (localhost) and /chat (portal full-screen) prefixes.
 */
export const normalizeAriaStandalonePath = (
  pathname: string,
  hostname?: string
): string => {
  const host = hostname ?? (typeof window !== 'undefined' ? window.location.hostname : '');
  const isSignupDomain = host === 'me.ariachat.ai' || host === 'signup.ariachat.ai';

  if (isSignupDomain) {
    return pathname;
  }
  if (pathname.startsWith('/aria')) {
    return pathname.replace(/^\/aria/, '') || '/';
  }
  if (isPortalFullScreenChatPath(pathname)) {
    return pathname.replace(/^\/chat/, '') || '/';
  }
  return pathname;
};

/**
 * Check if the current URL is an Aria URL
 * Detects based on hostname (ariachat.ai domains) or path (/aria prefix)
 */
export const isAriaUrl = (): boolean => {
  if (typeof window === 'undefined') {
    return false;
  }
  
  const hostname = window.location.hostname;
  const pathname = window.location.pathname;
  
  // Check if hostname contains ariachat.ai (production/staging)
  if (hostname.includes('ariachat.ai')) {
    return true;
  }
  
  // Check if path starts with /aria (development)
  if (pathname.startsWith('/aria')) {
    return true;
  }
  
  return false;
};

/** True for standalone Aria shell: ariachat.ai, /aria/*, or portal /chat. */
export const isStandaloneAriaShell = (): boolean => {
  return isAriaUrl() || isPortalFullScreenChatPath();
};

/**
 * Track that user has logged in before (for redirect logic)
 */
export const setAriaHasLoggedIn = (): void => {
  localStorage.setItem('aria_has_logged_in', 'true');
};

/**
 * Check if user has logged in before
 */
export const hasAriaLoggedIn = (): boolean => {
  return localStorage.getItem('aria_has_logged_in') === 'true';
};

/**
 * Clear the login history flag (on logout)
 */
export const clearAriaLoginHistory = (): void => {
  localStorage.removeItem('aria_has_logged_in');
};

/**
 * Get the create-account URL
 * Returns path without /aria for staging/production, with /aria for localhost
 */
export const getAriaCreateAccountUrl = (): string => {
  if (typeof window === 'undefined') {
    // Server-side fallback
    const baseUrl = getAriaAppBaseUrl();
    const pathPrefix = getAriaPathPrefix();
    return `${baseUrl}${pathPrefix}/create-account`;
  }
  // Use current origin to avoid double /aria issue
  const origin = window.location.origin;
  const pathPrefix = getAriaPathPrefix();
  return `${origin}${pathPrefix}/create-account`;
};

/**
 * Get the login URL
 * Returns path without /aria for staging/production, with /aria for localhost
 */
export const getAriaLoginUrl = (): string => {
  if (typeof window === 'undefined') {
    // Server-side fallback
    const baseUrl = getAriaAppBaseUrl();
    const pathPrefix = getAriaPathPrefix();
    return `${baseUrl}${pathPrefix}/login`;
  }
  // Use current origin to avoid double /aria issue
  const origin = window.location.origin;
  const pathPrefix = getAriaPathPrefix();
  return `${origin}${pathPrefix}/login`;
};

/**
 * Get the interests URL
 * Returns path without /aria for staging/production, with /aria for localhost
 */
export const getAriaInterestsUrl = (): string => {
  if (typeof window === 'undefined') {
    // Server-side fallback
    const baseUrl = getAriaAppBaseUrl();
    const pathPrefix = getAriaPathPrefix();
    return `${baseUrl}${pathPrefix}/interests`;
  }
  // Use current origin to avoid double /aria issue
  const origin = window.location.origin;
  const pathPrefix = getAriaPathPrefix();
  return `${origin}${pathPrefix}/interests`;
};

/**
 * Get a path for Aria routes with the correct prefix
 * @param path - The path without prefix (e.g., '/create-account', '/login')
 * @returns The full path with correct prefix based on environment
 */
export const getAriaPath = (path: string): string => {
  const pathPrefix = getAriaPathPrefix();
  return `${pathPrefix}${path}`;
};

/**
 * Get the appropriate redirect URL for unauthenticated users
 * Returns login if user has logged in before, otherwise create-account
 */
export const getAriaUnauthenticatedRedirectUrl = (): string => {
  const hasLoggedIn = hasAriaLoggedIn();
  return hasLoggedIn ? getAriaLoginUrl() : getAriaCreateAccountUrl();
};

 